import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

interface ScrollSyncApi {
  register(el: HTMLElement): () => void;
  /** Scroll every registered container to its left/right edge. The
   *  `leader` element animates smoothly; all other containers receive
   *  an instant scrollLeft write. See `scrollAllTo` for the why. */
  scrollAllToStart(leader: HTMLElement): void;
  scrollAllToEnd(leader: HTMLElement): void;
  /** Scroll every registered container to the given absolute scrollLeft
   *  (clamped per-element to its max). The `leader` element — typically
   *  the element the caller owns — animates smoothly so the user sees
   *  the motion; all other containers receive an **instant** scrollLeft
   *  write. We can't smooth-scroll the followers because
   *  `Element.scrollTo({behavior: "smooth"})` is unreliable on elements
   *  outside the viewport (browsers routinely skip the animation on
   *  off-screen scrollers), which would leave spot cards below the fold
   *  stuck at their previous scroll position. */
  scrollAllTo(left: number, leader: HTMLElement): void;
}

const ScrollSyncContext = createContext<ScrollSyncApi | null>(null);

/**
 * Provider that keeps a set of horizontal scroll containers in lockstep.
 * Mirrors the leader's scrollLeft to all followers immediately on each
 * scroll event so followers track without lag. The syncing flag prevents
 * the cascade of follower scroll events from re-triggering the mirror.
 */
export function ScrollSyncProvider({ children }: { children: ReactNode }) {
  const elementsRef = useRef<Set<HTMLElement>>(new Set());
  const syncingRef = useRef(false);
  const syncReleaseTimerRef = useRef<number | null>(null);

  const releaseSyncSoon = useCallback((delay: number) => {
    if (syncReleaseTimerRef.current !== null) window.clearTimeout(syncReleaseTimerRef.current);
    syncReleaseTimerRef.current = window.setTimeout(() => {
      syncingRef.current = false;
      syncReleaseTimerRef.current = null;
    }, delay);
  }, []);

  const register = useCallback((el: HTMLElement) => {
    // A container mounting after the others have already scrolled
    // (e.g. SpotCards remounting when the sort mode changes and their
    // keys flip) would otherwise start at scrollLeft = 0 and show the
    // wrong day-column under the day strip. Pick the first existing
    // member as the lockstep reference and align the newcomer to it.
    const leader = elementsRef.current.values().next().value;
    if (leader && leader !== el && el.scrollLeft !== leader.scrollLeft) {
      el.scrollLeft = leader.scrollLeft;
    }
    elementsRef.current.add(el);
    const onScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      const left = el.scrollLeft;
      for (const other of elementsRef.current) {
        if (other === el) continue;
        if (other.scrollLeft !== left) other.scrollLeft = left;
      }
      // Release on the next frame so the follower scroll events fired
      // by the writes above are still suppressed.
      requestAnimationFrame(() => {
        syncingRef.current = false;
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      elementsRef.current.delete(el);
    };
  }, []);

  const scrollAll = useCallback(
    (leader: HTMLElement, resolveLeft: (el: HTMLElement) => number) => {
      // Suppress the mirror — we're driving every container directly. The
      // leader's smooth-scroll progress events would otherwise re-fire the
      // mirror and fight our explicit follower writes.
      syncingRef.current = true;
      for (const el of elementsRef.current) {
        const target = resolveLeft(el);
        if (el === leader) {
          el.scrollTo({ left: target, behavior: "smooth" });
        } else {
          // Instant write — smooth-scroll on off-screen elements is
          // unreliable; instant writes are not. See the ScrollSyncApi
          // doc-comment above for the full rationale.
          el.scrollLeft = target;
        }
      }
      // Leader's smooth scroll fires events over hundreds of ms; release
      // on the trailing edge so the mirror stays quiet through the
      // animation.
      releaseSyncSoon(450);
    },
    [releaseSyncSoon],
  );

  const scrollAllToStart = useCallback(
    (leader: HTMLElement) => scrollAll(leader, () => 0),
    [scrollAll],
  );
  const scrollAllToEnd = useCallback(
    (leader: HTMLElement) =>
      scrollAll(leader, (el) => el.scrollWidth - el.clientWidth),
    [scrollAll],
  );
  const scrollAllToOffset = useCallback(
    (left: number, leader: HTMLElement) =>
      scrollAll(leader, (el) =>
        Math.max(0, Math.min(left, el.scrollWidth - el.clientWidth)),
      ),
    [scrollAll],
  );

  const api = useMemo<ScrollSyncApi>(
    () => ({
      register,
      scrollAllToStart,
      scrollAllToEnd,
      scrollAllTo: scrollAllToOffset,
    }),
    [register, scrollAllToStart, scrollAllToEnd, scrollAllToOffset],
  );

  return <ScrollSyncContext.Provider value={api}>{children}</ScrollSyncContext.Provider>;
}

/** Register a scroll container for sync. No-op outside a provider. */
export function useScrollSync(ref: RefObject<HTMLElement | null>) {
  const ctx = useContext(ScrollSyncContext);
  useEffect(() => {
    if (!ctx || !ref.current) return;
    return ctx.register(ref.current);
  }, [ctx, ref]);
}

/** Returns sync controls for the provider, or null if outside one.
 *  The returned object is memoized so consumers that put it in a
 *  `useEffect` dep array don't re-run on every render. */
export function useScrollSyncControls(): Pick<
  ScrollSyncApi,
  "scrollAllToStart" | "scrollAllToEnd" | "scrollAllTo"
> | null {
  const ctx = useContext(ScrollSyncContext);
  return useMemo(() => {
    if (!ctx) return null;
    return {
      scrollAllToStart: ctx.scrollAllToStart,
      scrollAllToEnd: ctx.scrollAllToEnd,
      scrollAllTo: ctx.scrollAllTo,
    };
  }, [ctx]);
}
